import { ChangeEvent, useEffect, useRef, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { FormattedMessage, useIntl } from "react-intl";
import { useEcgDetailsContext } from "../../../../context/EcgDetailsContext";
import useAlert from "../../../../hooks/useAlert";
import ecgsService from "../../../../services/ecgs.service";
import AlertModal from "../../../Shared/AlertModal/AlertModal";
import { Button, TextInput, Modal } from "@shltelemedicine/shl-ui-lib";
import CancelButton from "../../../Shared/CancelButton/CancelButton";
import FindingsIcon from "../../../Shared/FindingsIcon/FindingsIcon";
import useTextDirection from "../../../../hooks/useTextDirection";
import styles from "./styles.module.css";

type AddNoteModalProps = {
  show?: boolean;
  onNoteAdded?: () => void;
  onClose?: () => void;
};

const MAX_NOTE_LENGTH = 500;

const AddNoteModal = ({ show, onNoteAdded, onClose }: AddNoteModalProps) => {
  const {
    state: { selectedEcg },
  } = useEcgDetailsContext();

  const { showErrorAlert, alertProps, alertMessage } = useAlert();

  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState<string>();

  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (show) {
      setNote("");
      inputRef.current?.focus();
    }
  }, [show]);

  const noteChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    // if (event.currentTarget.value.length <= MAX_NOTE_LENGTH)
    setNote(event.currentTarget.value);
  };

  const addNoteHandler = async () => {
    try {
      setLoading(true);
      const { success } = await ecgsService.addNote(selectedEcg!.id, note!);

      if (!success) {
        showErrorAlert(
          <FormattedMessage
            id="ERROR.ECG.ADD_NOTE"
            defaultMessage="An error occurred while adding a note.<br></br>Please try again."
            values={{ br: () => <br></br> }}
          />
        );
        return;
      }

      if (onNoteAdded) {
        onNoteAdded();
      }
    } finally {
      setLoading(false);
    }
  };

  const addNotePlaceholder = intl.messages["GLOBAL.INSERT_HERE"] as string;

  const footerProps: FooterProps = {
    loading,
    disabled: loading || !note,
    addNoteHandler,
    onClose,
  };

  const textDirection = useTextDirection(note || "");

  return (
    <>
      <Modal
        centered
        backdrop="static"
        show={show}
        onHide={onClose}
        header={<Header />}
        footer={<Footer {...footerProps} />}
      >
        <div className="px-4 py-2">
          <TextInput
            className={textDirection === "rtl" ? styles.rtlDirection : ""}
            ref={inputRef}
            type="textarea"
            value={note}
            onChange={noteChangeHandler}
            placeholder={addNotePlaceholder}
            textareaRows={8}
          />
          {/* <span>
            {note?.length || 0}/{MAX_NOTE_LENGTH}
          </span> */}
        </div>
      </Modal>
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </>
  );
};

export default AddNoteModal;

const Header = () => (
  <div className="d-flex align-items-center">
    <FindingsIcon />
    <span className="mx-1"></span>
    <p className="mb-0">
      <FormattedMessage id="ECG_DETAILS.NOTES.ADD" defaultMessage="Add Note" />
    </p>
  </div>
);

type FooterProps = {
  loading?: boolean;
  disabled?: boolean;
  onClose?: () => void;
  addNoteHandler?: () => void;
};

const Footer = ({
  loading,
  disabled,
  addNoteHandler,
  onClose,
}: FooterProps) => {
  return (
    <div className="d-flex align-items-center justify-content-end border-top pt-2 px-3 m-sm-0">
      <CancelButton onClick={onClose} disabled={loading} />
      <span className="mx-2"></span>
      <Button
        bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
        disabled={disabled}
        onClick={addNoteHandler}
      >
        <span className="capitalized">
          <FormattedMessage id="GLOBAL.SUBMIT" defaultMessage="Submit" />
        </span>
        {loading && (
          <>
            &nbsp;&nbsp;
            <Spinner as="span" animation="border" size="sm" />
          </>
        )}
      </Button>
    </div>
  );
};
